<template>
  <div>
    <div>
      <v-dialog
        v-model="modalState"
        width="1200"
        persistent
      >
        <v-card>
          <v-card-title>
            Wound and Treatment Mapping
            <v-spacer></v-spacer>
            <btn
              color="secondary"
              class="buttons"
              :icon="icons.mdiSync"
              @click="resetWounds()"
            >
              Reset
            </btn>
            <btn
              color="success"
              class="buttons"
              :icon="icons.mdiContentSaveEdit"
              @click="saveWounds()"
            >
              Save
            </btn>
            <btn
              color="primary"
              class="buttons"
              :icon="icons.mdiEyeOutline"
              @click="viewJson()"
            >
              View
            </btn>
            <btn
              color="secondary"
              class="buttons"
              :icon="icons.mdiClose"
              @click="closeModal()"
            >
              Close
            </btn>
          </v-card-title>

          <v-card-text>
            <alert color="primary">
              Review the detected treatments and merge them to existing wounds/treatments, add them as new wounds, edit, or remove them.
            </alert>
            <div
              class="ma-1"
            >
              <div class="title mt-6">
                New Wounds
              </div>
              <div>
                These are the treatments detected from your dictation, please select what you want to do with them:
              </div>
              <WoundMappingCard
                v-model="wounds"
                class="mt-2"
                :generated-wound-list.sync="generatedWounds"
                :encounter-id="encounterId"
                :patient-id="patientId"
                :visit-date="visitDate"
                :is-circulation-compromised="isCirculationCompromised"
                :merged-wounds-initial.sync="mergedWounds"
              ></WoundMappingCard>
            </div>
          </v-card-text>
        </v-card>
        <v-dialog
          v-model="viewJsonModalState"
          width="1200"
          persistent
        >
          <v-card>
            <v-card-title>
              Wound and Treatment Mapping
              <v-spacer></v-spacer>
              <btn
                label="Clip Json"
                :icon="icons.mdiContentCopy"
                @click="copyJsonToClipboard"
              ></btn>
              <btn
                color="secondary"
                class="buttons"
                :icon="icons.mdiClose"
                @click="viewJsonModalState = false"
              >
                Close
              </btn>
            </v-card-title>

            <v-card-text>
              <textarea
                ref="json"
                v-model="resultsString"
                style="height:350px;border:thin solid #777;border-radius:5px;"
                class="col-sm-12"
              ></textarea>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-dialog>
    </div>
  </div>
</template>

<script>

import {
  mdiClose,
  mdiContentCopy,
  mdiContentSaveEdit,
  mdiEyeOutline,
  mdiHexagram,
  mdiInformationOutline,
  mdiSync,
} from '@mdi/js'
import { v4 as uuidv4 } from 'uuid'
import { mapGetters } from 'vuex'
import { mergeIfEmpty } from '@/utils/mergeIfEmpty'
import WoundMappingCard from './WoundMappingCard.vue'

export default {
  components: {
    WoundMappingCard,
  },
  props: {
    value: {
      type: Array,
      default: null,
    },
    woundMappingModal: {
      type: Boolean,
      default: false,
    },
    encounterId: {
      type: String,
      default: null,
    },
    encounter: {
      type: Object,
      default: () => {},
    },
    patientId: {
      type: String,
      default: null,
    },
    visitDate: {
      type: String,
      default: null,
    },
    woundTreatmentUpdated: {
      type: Boolean,
      default: null,
    },
    isCirculationCompromised: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      woundTypeStructure: {
        wound_number: null,
        practice_type_id: 1,
        is_wound_facility_acquired: false,
        is_wound_not_recent: false,
        wound_acquired_facility_id: null,
        amnio_pre_treatment_duration_weeks: null,
        location: {
          wound_id: null,
          left_right: null,
          quadrant: null,
          section: null,
          inferior_superior: null,
          proximal_distal: null,
          anterior_posterior: null,
          medial_lateral_bilateral: null,
          dorsal_plantar: null,
          digits: null,
          finger_toe: null,
          location: null,
          optional_modifier: null,
          location_text: null,
          modified: null,
        },
        id: null,
        patient_id: null,
        encounter_id: null,
        provider_user_id: null,
        is_healed: false,
        is_poor_healing: false,
        created: null,
        place_of_service_id: null,
        can_be_deleted: true,
        followup_treatment: false,
        updated: true,
        treatments: [],
        modified: null,
      },
      dermTypeStructure: {
        wound_number: null,
        practice_type_id: 6,
        location: {
          wound_id: null,
          left_right: null,
          quadrant: null,
          section: null,
          inferior_superior: null,
          proximal_distal: null,
          anterior_posterior: null,
          medial_lateral_bilateral: null,
          dorsal_plantar: null,
          digits: null,
          finger_toe: null,
          location: null,
          optional_modifier: null,
          location_text: null,
          modified: null,
        },
        id: null,
        patient_id: null,
        encounter_id: null,
        provider_user_id: null,
        is_healed: false,
        is_poor_healing: false,
        created: null,
        is_wound_facility_acquired: false,
        is_wound_not_recent: false,
        wound_acquired_facility_id: null,
        place_of_service_id: null,
        can_be_deleted: true,
        followup_treatment: false,
        updated: true,
        treatments: [],
        modified: null,
      },
      gtubeTypeStructure: {
        wound_number: null,
        practice_type_id: 5,
        id: null,
        patient_id: null,
        encounter_id: null,
        provider_user_id: null,
        is_healed: false,
        is_poor_healing: false,
        created: null,
        is_wound_facility_acquired: false,
        is_wound_not_recent: false,
        wound_acquired_facility_id: null,
        place_of_service_id: null,
        can_be_deleted: true,
        followup_treatment: false,
        updated: true,
        treatments: [],
        modified: null,
      },

      woundTreatmentStructure: {
        id: null,
        place_of_service_id: null,
        patient_id: null,
        encounter_id: null,
        wound_id: null,
        etiology: null,
        etiology_other: null,
        procedure_id: null,
        current_treatment: null,
        qent_notes: null,
        qent_recommendation: null,
        operative_note: null,
        is_validated: false,
        is_quick_note_validated: false,
        created: null,
        encounter: {
          id: null,
          visit_date: null,
        },
        require_vitals: true,
        can_be_deleted: true,
        wound_treatment: {
          wound_treatment_wound_size: {
            primary_length: null,
            primary_width: null,
            primary_depth: null,
            primary_area: null,
            slough: null,
            granulation: null,
            necrotic_tissue: null,
            hyper_granulation: null,
            eschar: null,
            epithelial: null,
            dermal: null,
            pre_debridement_total: null,
            unhealthy_granulation: false,
            wound_stage: null,
            post_debridement_length: null,
            post_debridement_width: null,
            post_debridement_depth: null,
            post_debridement_area: null,
            post_debridement_percent: null,
            post_debridement_area_debrided: null,
            wound_size_text: null,
          },
          progress: null,
          progress_other: null,
          odors: [],
          exudate_amounts: [],
          exudate_types: [],
          periwounds: [],
          wound_edges: [],
          infection_signs: [],
          wound_treatment_undermining: {
            inside_quadrant_1: null,
            inside_quadrant_2: null,
            inside_quadrant_3: null,
            inside_quadrant_4: null,
            outside_1_oclock: null,
            outside_2_oclock: null,
            outside_3_oclock: null,
            outside_4_oclock: null,
            outside_5_oclock: null,
            outside_6_oclock: null,
            outside_7_oclock: null,
            outside_8_oclock: null,
            outside_9_oclock: null,
            outside_10_oclock: null,
            outside_11_oclock: null,
            outside_12_oclock: null,
          },
          wound_treatment_tunneling: {
            outside_1_oclock: null,
            outside_2_oclock: null,
            outside_3_oclock: null,
            outside_4_oclock: null,
            outside_5_oclock: null,
            outside_6_oclock: null,
            outside_7_oclock: null,
            outside_8_oclock: null,
            outside_9_oclock: null,
            outside_10_oclock: null,
            outside_11_oclock: null,
            outside_12_oclock: null,
          },
          wound_treatment_sinus_tract: {
            outside_1_oclock: null,
            outside_2_oclock: null,
            outside_3_oclock: null,
            outside_4_oclock: null,
            outside_5_oclock: null,
            outside_6_oclock: null,
            outside_7_oclock: null,
            outside_8_oclock: null,
            outside_9_oclock: null,
            outside_10_oclock: null,
            outside_11_oclock: null,
            outside_12_oclock: null,
          },
          pain_level: null,
          wound_description_summary: null,
          preoperative_indications: [],
          preoperative_indication_other: null,
          dressing_used_1: null,
          dressing_used_2: null,
          dressing_used_other: null,
          reason_for_dakins_solution: null,
          debridement_not_performed_reasons: [],
          debridement_not_performed_reason_other: null,
          ongoing_debridement_reason: '',
          ongoing_debridement_reason_other: null,
          current_treatment: null,
          qent_notes: null,
          operative_note_wizard: {},
          operative_note: null,
          amnio_grafts: [],
          wound_treatment_amnio_fixations: [],
          wound_treatments_amnios: {},
          wound_treatments_amnios_research: {},
          wound_treatments_amnios_researches_symptoms: [],
        },
        updated: true,
        modified: null,
      },
      dermTreatmentStructure: {
        id: null,
        place_of_service_id: null,
        patient_id: null,
        encounter_id: null,
        wound_id: null,
        etiology: null,
        etiology_other: null,
        procedure_id: null,
        current_treatment: null,
        qent_notes: null,
        qent_recommendation: null,
        operative_note: null,
        is_validated: false,
        is_quick_note_validated: false,
        created: null,
        encounter: {
          id: null,
          visit_date: null,
        },
        require_vitals: null,
        can_be_deleted: true,
        derm_treatment: {
          lesion_conditions: [],
          lesion_conditions_other: null,
          lesion_description: null,
          topical_agent: null,
          topical_agent_strength: null,
          secondary_topical_agent: null,
          secondary_topical_agent_strength: null,
          current_treatment: null,
          qent_notes: null,
          qent_recommendation: null,
          operative_note: null,
          duration: null,
        },
        updated: true,
        modified: null,
      },
      gtubeTreatmentStructure: {
        id: null,
        place_of_service_id: null,
        patient_id: null,
        encounter_id: null,
        wound_id: null,
        etiology: null,
        etiology_other: null,
        procedure_id: null,
        current_treatment: null,
        qent_notes: null,
        qent_recommendation: null,
        operative_note: null,
        is_validated: false,
        is_quick_note_validated: true,
        created: null,
        encounter: {
          id: null,
          visit_date: null,
        },
        require_vitals: null,
        can_be_deleted: true,
        g_tube_treatment: {
          abdominal_exam: null,
          abdominal_exam_other: null,
          g_tube_type: null,
          g_tube_type_other: null,
          peri_tube_finding: null,
          duration: null,
          peri_tube_tract: null,
          replacement_tube_type: null,
          replacement_tube_type_other: null,
          balloon_cc: null,
          replacement_size: null,
          replacement_reason: null,
          replacement_reason_other: null,
          verification_of_placement_1: null,
          verification_of_placement_2: null,
          verification_of_placement_3: null,
          verification_of_placement_other: null,
          current_treatment: null,
          qent_notes: null,
          qent_recommendation: null,
          operative_note: null,
        },
        updated: true,
        modified: null,
      },
      opNoteStructure: {
        treatment_id: null,
        consent_selected: null,
        consent_text: null,
        positioning_id: null,
        cleansing_id: null,
        cleansing_2_id: null,
        pain_management_id: null,
        pain_management_2_id: null,
        incision_type_id: null,
        instrument_id: null,
        instrument_2_id: null,
        instrument_3_id: null,
        visualized_bone_id: null,
        visualized_bone_other_text: null,
        visualized_muscle_id: null,
        visualized_muscle_other_text: null,
        estimated_blood_loss_id: null,
        hemostasis_id: null,
        hemostasis_2_id: null,
        hemostasis_text: null,
        pain_management_other: null,
      },
      initialExistingWounds: [...this.$lodash.cloneDeep(this.value)],
      initialGeneratedWounds: [],
      initialEncounter: {},
      initialMergedWounds: [],
      icons: {
        mdiHexagram,
        mdiClose,
        mdiSync,
        mdiInformationOutline,
        mdiContentSaveEdit,
        mdiEyeOutline,
        mdiContentCopy,
      },
      viewJsonModalState: false,
      resultsString: null,

      wounds: [...this.$lodash.cloneDeep(this.value)],
      generatedWounds: [],
      generatedEncounter: {},
      mergedWounds: [],
    }
  },
  computed: {
    ...mapGetters('encounters', ['medicalHistories', 'neuropathyTypes',
      'neuropathySeverities', 'medications', 'riskFactors', 'periwounds',
      'exudateTypes', 'exudateAmounts', 'odors', 'woundEdges', 'infectionSigns',
      'dressings', 'gTubeTypes', 'lesionConditions', 'woundEtiologies', 'dermEtiologies',
    ]),
    modalState() {
      return this.woundMappingModal
    },
  },
  watch: {
    modalState(newValue) {
      if (newValue === true) {
        this.generatedWounds = []
        this.mergedWounds = []
        this.initialExistingWounds = [...this.$lodash.cloneDeep(this.value)]

        // Get AI results and run formatToDatabase function to extract and format response data
        const results = this.$store.state.patients.aiResults

        // for debugging local json files
        // const results = this.example
        this.resultsString = JSON.stringify(results, null, 2)
        this.formatToDatabase(results)

        this.initialGeneratedWounds = [...this.$lodash.cloneDeep(this.generatedWounds)]
        this.initialMergedWounds = [...this.$lodash.cloneDeep(this.mergedWounds)]
      }
    },
    value: {
      deep: true,
      handler(newValue) {
        this.wounds = [...this.$lodash.cloneDeep(newValue)]
      },
    },
  },
  created() {
  },
  mounted() {
  },
  methods: {
    formatToDatabase(json) {
      const woundNumber = this.wounds.length > 0
        ? Math.max(...this.wounds.map(o => o.wound_number)) + 1
        : 1

      let nextWoundNumber = woundNumber
      json.wounds.forEach(wound => {
        let practiceType = null
        switch (true) {
          case (wound.hasOwnProperty('wound_treatments')):
            practiceType = 1
            break
          case (wound.hasOwnProperty('derm_treatment')):
            practiceType = 6
            break
          case (wound.hasOwnProperty('gtube_treatment')):
            practiceType = 5
            break
          default:
            break
        }
        if (((practiceType === 1 || practiceType === 6) && wound.context?.location) || practiceType === 5) {
          const generatedWound = this.generateWound(wound, nextWoundNumber, practiceType)
          generatedWound.treatments.push(this.generateTreatment(wound, generatedWound.id))
          this.generatedWounds.push(generatedWound)
          nextWoundNumber += 1
        }
      })

      this.formatEncounter(json.encounter)
    },
    generateWound(woundObj, woundNum, practiceType) {
      const woundId = uuidv4()
      const userId = this.$scribeProvider.id ?? this.$authUser.id()
      let woundStructure = null
      switch (true) {
        case (practiceType === 1):
          woundStructure = { ...this.$lodash.cloneDeep(this.woundTypeStructure) }
          break
        case (practiceType === 6):
          woundStructure = { ...this.$lodash.cloneDeep(this.dermTypeStructure) }
          break
        case (practiceType === 5):
          woundStructure = { ...this.$lodash.cloneDeep(this.gtubeTypeStructure) }
          break
        default:
          break
      }

      // data retrieved from AI
      // left is property name for our database, right side is AI property name
      // is_wound_facility_acquired : is_facility_acquired
      // ONLY FOR WOUND/DERM
      // location.location_text : location
      const wound = {
        ...woundStructure,

        id: woundId,
        patient_id: this.patientId,
        encounter_id: this.encounterId,
        provider_user_id: userId,
        place_of_service_id: this.placeOfServiceId,
        wound_number: woundNum,
        created: this.$custom.utcNow(),

        is_wound_facility_acquired: woundObj.context?.is_facility_acquired ? woundObj.context.is_facility_acquired : false,
      }

      if (practiceType === 1 || practiceType === 6) {
        wound.location.location_text = woundObj.context?.location ? woundObj.context.location : null
        wound.location.optional_modifier = woundObj.context?.location ? woundObj.context.location : null
        if (this.initialExistingWounds.length > 0 && wound.location.location_text) {
          const index = this.initialExistingWounds
            .findIndex(existingWound => (existingWound.location?.location_text?.toLowerCase() === wound.location.location_text.toLowerCase())
          && (existingWound.practice_type_id === practiceType))
          if (index >= 0) this.mergedWounds.push({ generatedId: woundId, existingId: this.initialExistingWounds[index].id })
        }

        if (woundObj.wound_treatments?.amnio) {
          wound.amnio_pre_treatment_duration_weeks = woundObj.wound_treatments?.amnio?.ulcer_duration_weeks ? woundObj.wound_treatments?.amnio?.ulcer_duration_weeks : null
        }
      }

      return wound
    },
    generateTreatment(woundObj, woundId) {
      let treatmentStructure = null
      const treatmentId = uuidv4()
      let practiceType = null

      switch (true) {
        case (woundObj.hasOwnProperty('wound_treatments')):
          treatmentStructure = this.formatToWoundTreatment(woundObj.wound_treatments[0], treatmentId)
          practiceType = 1
          break
        case (woundObj.hasOwnProperty('derm_treatment')):
          treatmentStructure = this.formatToDermTreatment(woundObj.derm_treatment[0])
          practiceType = 6
          break
        case (woundObj.hasOwnProperty('gtube_treatment')):
          treatmentStructure = this.formatToGtubeTreatment(woundObj.gtube_treatment[0])
          practiceType = 5
          break
        default:
          break
      }

      const treatment = {
        ...treatmentStructure,

        id: treatmentId,
        place_of_service_id: this.placeOfServiceId,
        patient_id: this.patientId,
        encounter_id: this.encounterId,
        wound_id: woundId,
        created: this.$custom.utcNow(),
        encounter: {
          id: this.encounterId,
          visit_date: this.visitDate,
        },

        // Not in the DB, for Vue only
        require_vitals: null,
        can_be_deleted: true,
      }

      if (practiceType === 1 || practiceType === 6) {
        if (woundObj.context?.etiology) {
          const jsonEtiology = woundObj.context.etiology
          let found = null
          if (practiceType === 1) {
            found = this.woundEtiologies.find(i => i.title.toLowerCase().includes(jsonEtiology.toLowerCase()))
          } else if (practiceType === 6) {
            found = this.dermEtiologies.find(i => i.title.toLowerCase().includes(jsonEtiology.toLowerCase()))
          }
          treatment.etiology = found ? found.title : null
        }

        if (woundObj.context?.etiology_other) {
          treatment.etiology = 'Other'
          treatment.etiology_other = woundObj.context?.etiology_other ? woundObj.context.etiology_other : null
        }
      }

      return treatment
    },
    formatToWoundTreatment(treatmentObj, treatmentId) {
      const treatment = {
        ...this.$lodash.cloneDeep(this.woundTreatmentStructure),

        procedure_id: treatmentObj.procedure_performed ? treatmentObj.procedure_performed : null,
      }

      treatment.wound_treatment = {
        ...treatment.wound_treatment,
        progress: treatmentObj.wound_progress ? treatmentObj.wound_progress : null,
        progress_other: treatmentObj.wound_progress_other ? treatmentObj.wound_progress_other : null,
      }

      // data retrieved from AI
      // left is property name for our database, right side is AI property name
      if (treatmentObj.reasons_debridement_not_performed) {
        treatment.debridement_not_performed_reasons = treatmentObj.reasons_debridement_not_performed.reasons ? treatmentObj.reasons_debridement_not_performed.reasons : []
        treatment.debridement_not_performed_reason_other = treatmentObj.reasons_debridement_not_performed.other_reason ? treatmentObj.reasons_debridement_not_performed.other_reason : null
      }

      if (treatmentObj.wound_measurements) {
        // treatment.wound_measurements.resolved = treatmentObj.wound_measurements?.is_resolved ? treatmentObj.wound_measurements?.is_resolved : false
        if (treatmentObj.wound_measurements.primary) {
          const primary = { ...treatmentObj.wound_measurements.primary }
          treatment.wound_treatment.wound_treatment_wound_size = {
            ...treatment.wound_treatment.wound_treatment_wound_size,
            primary_length: primary.length_cm ? primary.length_cm : null,
            primary_width: primary.width_cm ? primary.width_cm : null,
            primary_depth: primary.depth_cm ? primary.depth_cm : null,
            primary_area: primary.area_cm2 ? primary.area_cm2 : null,
            wound_size_text: this.calcWoundSize(primary.length_cm, primary.width_cm, primary.depth_cm),
          }
        }
        if (treatmentObj.wound_measurements.pre_debridement_bed) {
          const preDebridement = { ...treatmentObj.wound_measurements.pre_debridement_bed }
          treatment.wound_treatment.wound_treatment_wound_size = {
            ...treatment.wound_treatment.wound_treatment_wound_size,
            slough: preDebridement.slough_pct ? preDebridement.slough_pct : null,
            granulation: preDebridement.granulation_pct ? preDebridement.granulation_pct : null,
            necrotic_tissue: preDebridement.necrotic_pct ? preDebridement.necrotic_pct : null,
            hyper_granulation: preDebridement.hyper_granulation_pct ? preDebridement.hyper_granulation_pct : null,
            eschar: preDebridement.eschar_pct ? preDebridement.eschar_pct : null,
            epithelial: preDebridement.epithelial_pct ? preDebridement.epithelial_pct : null,
            dermal: preDebridement.dermal_pct ? preDebridement.dermal_pct : null,
            unhealthy_granulation: preDebridement.unhealthy_granulation ? preDebridement.unhealthy_granulation : false,
            wound_stage: preDebridement.wound_stage ? preDebridement.wound_stage : null,
          }
        }
        if (treatmentObj.wound_measurements.post_debridement) {
          const postDebridement = { ...treatmentObj.wound_measurements.post_debridement }
          treatment.wound_treatment.wound_treatment_wound_size = {
            ...treatment.wound_treatment.wound_treatment_wound_size,
            post_debridement_length: postDebridement.length_cm ? postDebridement.length_cm : null,
            post_debridement_width: postDebridement.width_cm ? postDebridement.width_cm : null,
            post_debridement_depth: postDebridement.depth_cm ? postDebridement.depth_cm : null,
            post_debridement_area: postDebridement.area_cm2 ? postDebridement.area_cm2 : null,
            post_debridement_percent: postDebridement.percent_debrided ? postDebridement.percent_debrided : null,
            post_debridement_area_debrided: postDebridement.area_debrided_cm2 ? postDebridement.area_debrided_cm2 : null,
          }
        }
      }

      if (treatmentObj.wound_description) {
        if (treatmentObj.wound_description.odor) {
          const found = this.odors.find(i => i.title.toLowerCase().includes(treatmentObj.wound_description.odor.toLowerCase()))
          treatment.wound_treatment.odors = [found]
        }
        if (treatmentObj.wound_description.exudate_amount) {
          const found = this.exudateAmounts.find(i => i.title.toLowerCase().includes(treatmentObj.wound_description.exudate_amount.toLowerCase()))
          treatment.wound_treatment.exudate_amounts = [found]
        }

        const exudateTypeList = []
        if (treatmentObj.wound_description.exudate_type) {
          const normalize = Array.isArray(treatmentObj.wound_description.exudate_type) ? treatmentObj.wound_description.exudate_type : [treatmentObj.wound_description.exudate_type]
          normalize.forEach(item => {
            const found = this.exudateTypes.find(i => i.title.toLowerCase().includes(item.toLowerCase()))
            if (found) exudateTypeList.push(found)
          })
        }
        treatment.wound_treatment.exudate_types = exudateTypeList

        const periwoundList = []
        if (treatmentObj.wound_description.periwound) {
          const normalize = Array.isArray(treatmentObj.wound_description.periwound) ? treatmentObj.wound_description.periwound : [treatmentObj.wound_description.periwound]
          normalize.forEach(item => {
            const found = this.periwounds.find(i => i.title.toLowerCase().includes(item.toLowerCase()))
            if (found) periwoundList.push(found)
          })
        }
        treatment.wound_treatment.periwounds = periwoundList

        const woundEdgesList = []
        if (treatmentObj.wound_description.wound_edge) {
          const normalize = Array.isArray(treatmentObj.wound_description.wound_edge) ? treatmentObj.wound_description.wound_edge : [treatmentObj.wound_description.wound_edge]
          normalize.forEach(item => {
            const found = this.woundEdges.find(i => i.title.toLowerCase().includes(item.toLowerCase()))
            if (found) woundEdgesList.push(found)
          })
        }
        treatment.wound_treatment.wound_edges = woundEdgesList
        const infectionSignsList = []
        if (treatmentObj.wound_description.signs_of_infection) {
          const normalize = Array.isArray(treatmentObj.wound_description.signs_of_infection) ? treatmentObj.wound_description.signs_of_infection : [treatmentObj.wound_description.signs_of_infection]
          normalize.forEach(item => {
            const found = this.infectionSigns.find(i => i.title.toLowerCase().includes(item.toLowerCase()))
            if (found) infectionSignsList.push(found)
          })
        }
        treatment.wound_treatment.infection_signs = infectionSignsList
        treatment.wound_treatment.pain_level = treatmentObj.wound_description?.pain_level ? treatmentObj.wound_description.pain_level : null
        treatment.wound_treatment.wound_description_summary = treatmentObj.wound_description?.summary ? treatmentObj.wound_description.summary : null
        treatment.wound_treatment.preoperative_indications = treatmentObj.wound_treatment?.preoperative_indications ? treatmentObj.wound_treatment.preoperative_indications : []
        treatment.wound_treatment.preoperative_indications_other = treatmentObj.wound_treatment?.preoperative_indications_other ? treatmentObj.wound_treatment.preoperative_indications_other : null
        if (treatmentObj.wound_description.undermining) {
          const undermining = { ...treatmentObj.wound_description.undermining }
          treatment.wound_treatment.wound_treatment_undermining = {
            inside_quadrant_1: undermining.quadrant_1 ? undermining.quadrant_1 : null,
            inside_quadrant_2: undermining.quadrant_2 ? undermining.quadrant_2 : null,
            inside_quadrant_3: undermining.quadrant_3 ? undermining.quadrant_3 : null,
            inside_quadrant_4: undermining.quadrant_4 ? undermining.quadrant_4 : null,
            outside_12_oclock: undermining.h12 ? undermining.h12 : null,
            outside_1_oclock: undermining.h1 ? undermining.h1 : null,
            outside_2_oclock: undermining.h2 ? undermining.h2 : null,
            outside_3_oclock: undermining.h3 ? undermining.h3 : null,
            outside_4_oclock: undermining.h4 ? undermining.h4 : null,
            outside_5_oclock: undermining.h5 ? undermining.h5 : null,
            outside_6_oclock: undermining.h6 ? undermining.h6 : null,
            outside_7_oclock: undermining.h7 ? undermining.h7 : null,
            outside_8_oclock: undermining.h8 ? undermining.h8 : null,
            outside_9_oclock: undermining.h9 ? undermining.h9 : null,
            outside_10_oclock: undermining.h10 ? undermining.h10 : null,
            outside_11_oclock: undermining.h11 ? undermining.h11 : null,
          }
        }
        if (treatmentObj.wound_description.tunneling) {
          const tunneling = { ...treatmentObj.wound_description.tunneling }
          treatment.wound_treatment.wound_treatment_tunneling = {
            outside_12_oclock: tunneling.h12 ? tunneling.h12 : null,
            outside_1_oclock: tunneling.h1 ? tunneling.h1 : null,
            outside_2_oclock: tunneling.h2 ? tunneling.h2 : null,
            outside_3_oclock: tunneling.h3 ? tunneling.h3 : null,
            outside_4_oclock: tunneling.h4 ? tunneling.h4 : null,
            outside_5_oclock: tunneling.h5 ? tunneling.h5 : null,
            outside_6_oclock: tunneling.h6 ? tunneling.h6 : null,
            outside_7_oclock: tunneling.h7 ? tunneling.h7 : null,
            outside_8_oclock: tunneling.h8 ? tunneling.h8 : null,
            outside_9_oclock: tunneling.h9 ? tunneling.h9 : null,
            outside_10_oclock: tunneling.h10 ? tunneling.h10 : null,
            outside_11_oclock: tunneling.h11 ? tunneling.h11 : null,
          }
        }
        if (treatmentObj.wound_description.sinus_tract) {
          const sinus_tract = { ...treatmentObj.wound_description.sinus_tract }
          treatment.wound_treatment.wound_treatment_sinus_tract = {
            outside_12_oclock: sinus_tract.h12 ? sinus_tract.h12 : null,
            outside_1_oclock: sinus_tract.h1 ? sinus_tract.h1 : null,
            outside_2_oclock: sinus_tract.h2 ? sinus_tract.h2 : null,
            outside_3_oclock: sinus_tract.h3 ? sinus_tract.h3 : null,
            outside_4_oclock: sinus_tract.h4 ? sinus_tract.h4 : null,
            outside_5_oclock: sinus_tract.h5 ? sinus_tract.h5 : null,
            outside_6_oclock: sinus_tract.h6 ? sinus_tract.h6 : null,
            outside_7_oclock: sinus_tract.h7 ? sinus_tract.h7 : null,
            outside_8_oclock: sinus_tract.h8 ? sinus_tract.h8 : null,
            outside_9_oclock: sinus_tract.h9 ? sinus_tract.h9 : null,
            outside_10_oclock: sinus_tract.h10 ? sinus_tract.h10 : null,
            outside_11_oclock: sinus_tract.h11 ? sinus_tract.h11 : null,
          }
        }
      }

      if (treatmentObj.dressings_used) {
        const dressingsObj = { ...treatmentObj.dressings_used }
        treatment.wound_treatment.dressing_used_1 = dressingsObj.primary ? dressingsObj.primary : null
        treatment.wound_treatment.dressing_used_2 = dressingsObj.additional ? dressingsObj.additional : null
        treatment.wound_treatment.dressing_used_other = dressingsObj.other ? dressingsObj.other : null
        treatment.wound_treatment.reason_for_dakins_solution = dressingsObj.reason_for_dakins ? dressingsObj.reason_for_dakins : null
      }

      if (treatmentObj.reason_for_ongoing_debridement) {
        const ongoingDebridement = { ...treatmentObj.reason_for_ongoing_debridement }
        treatment.wound_treatment.ongoing_debridement_reason = ongoingDebridement.selections ? ongoingDebridement.selections : ''
        treatment.wound_treatment.ongoing_debridement_reason_other = ongoingDebridement.other ? ongoingDebridement.other : null
      }

      if (treatmentObj.operative_note) {
        const opNote = { ...treatmentObj.operative_note }
        treatment.operative_note = opNote.text ? opNote.text : null
        const opNoteWizard = {}
        if (opNote.consent) {
          opNoteWizard.consent_selected = opNote.consent.obtained_from ? opNote.consent.obtained_from : null
          opNoteWizard.consent_text = opNote.consent.description ? opNote.consent.description : null
        }
        opNoteWizard.positioning_id = opNote.positioning ? opNote.positioning : null

        if (treatmentObj.operative_note.cleansing) {
          const cleansing = { ...treatmentObj.operative_note.cleansing }
          opNoteWizard.cleansing_id = cleansing.method ? cleansing.method : null
          opNoteWizard.cleansing_2_id = cleansing.other ? cleansing.other : null
        }
        if (treatmentObj.operative_note.pain_management) {
          const pain_management = { ...treatmentObj.operative_note.pain_management }
          opNoteWizard.pain_management_id = pain_management.method_1 ? pain_management.method_1 : null
          opNoteWizard.pain_management_2_id = pain_management.method_2 ? pain_management.method_2 : null
          opNoteWizard.pain_management_other = pain_management.other ? pain_management.other : null
        }
        if (treatmentObj.operative_note.technique) {
          const technique = { ...treatmentObj.operative_note.technique }
          opNoteWizard.incision_type_id = technique.incision_type ? technique.incision_type : null
          opNoteWizard.instrument_id = technique.instrument ? technique.instrument : null
          opNoteWizard.instrument_2_id = technique.additional_instrument_1 ? technique.additional_instrument_1 : null
          opNoteWizard.instrument_3_id = technique.additional_instrument_2 ? technique.additional_instrument_2 : null
        }
        if (treatmentObj.operative_note.visualized_tissue) {
          const visualized_tissue = { ...treatmentObj.operative_note.visualized_tissue }
          opNoteWizard.visualised_bone_id = visualized_tissue.bone ? visualized_tissue.bone : null
          opNoteWizard.visualised_bone_other = visualized_tissue.bone_other ? visualized_tissue.bone_other : null
          opNoteWizard.visualised_muscle_id = visualized_tissue.muscle ? visualized_tissue.muscle : null
          opNoteWizard.visualised_muscle_other_text = visualized_tissue.muscle_other ? visualized_tissue.muscle_other : null
        }
        if (treatmentObj.operative_note.ebl_hemostasis_dressings) {
          const hemostasis_dressings = { ...treatmentObj.operative_note.hemostasis_dressings }
          opNoteWizard.estimated_blood_loss_id = hemostasis_dressings.estimated_blood_loss_cc ? hemostasis_dressings.estimated_blood_loss_cc : null
          opNoteWizard.hemostasis_id = hemostasis_dressings.hemostasis ? hemostasis_dressings.hemostasis : null
          opNoteWizard.hemostasis_text = hemostasis_dressings.hemostasis_other ? hemostasis_dressings.hemostasis_other : null
          opNoteWizard.hemostasis_2_id = hemostasis_dressings.additional_hemostasis ? hemostasis_dressings.additional_hemostasis : null
          treatment.dressing_used_1 = hemostasis_dressings.dressing_used ? hemostasis_dressings.dressing_used : null
          treatment.dressing_used_2 = hemostasis_dressings.additional_dressing ? hemostasis_dressings.additional_dressing : null
          treatment.dressing_used_other = hemostasis_dressings.other_dressings ? hemostasis_dressings.other_dressings : null
        }

        treatment.operative_note_wizard = opNoteWizard
      }

      if (treatmentObj.amnio) {
        // if amnio data is detected, change procedure_id to 178
        treatment.procedure_id = 178
        const amnio = { ...treatmentObj.amnio }

        let woundTreatmentAmnioFixations = []
        const woundTreatmentsAmnios = {}
        const woundTreatmentsAmniosResearch = {}
        const woundTreatmentsAmniosResearchSymptoms = {}
        const amnio_grafts = []

        woundTreatmentsAmnios.reason_required = amnio.reason_required ? amnio.reason_required : null
        woundTreatmentsAmnios.secondary_procedure = amnio.initial_procedure_performed ? amnio.initial_procedure_performed : null
        woundTreatmentsAmnios.thickness = amnio.wound_thickness ? amnio.wound_thickness : null
        woundTreatmentsAmnios.tobacco_cessation = amnio.tobacco_cessation?.patient_smoker_status ? amnio.tobacco_cessation.patient_smoker_status : null
        woundTreatmentsAmnios.is_duplex_ultrasound_performed = amnio.vlu_duplex_ultrasound_performed ? amnio.vlu_duplex_ultrasound_performed : null
        woundTreatmentsAmnios.is_no_osteomyelitis = amnio.absence_of_osteomyelitis_confirmed ? amnio.absence_of_osteomyelitis_confirmed : null
        woundTreatmentsAmnios.is_adequate_circulation = amnio.adequate_blood_circulation_confirmed ? amnio.adequate_blood_circulation_confirmed : null
        woundTreatmentsAmnios.is_no_exudate = amnio.minimal_exudate_confirmed ? amnio.minimal_exudate_confirmed : null
        woundTreatmentsAmnios.application_notes = amnio.application_notes ? amnio.application_notes : null
        woundTreatmentsAmnios.product_wasted = amnio.waste_status ? amnio.waste_status : null
          woundTreatmentsAmnios.quantity_wasted = amnio.quantity_wasted_cm2 ? amnio.quantity_wasted_cm2 : null
          woundTreatmentsAmnios.reason_wasted = amnio.reason_wasted ? amnio.reason_wasted : null

        if (amnio.fixation_method) {
          const fixation_method = [...treatmentObj.amnio.fixation_method]
          const fixationsList = []
          const normalize = Array.isArray(fixation_method) ? fixation_method : [fixation_method]
          normalize.forEach(x => {
            fixationsList.push({
              id: x.id,
              treatment_id: treatmentId,
            })
          })
          woundTreatmentAmnioFixations = fixationsList
        }
        if (treatmentObj.amnio.time_of_service) {
          const time_of_service = { ...treatmentObj.amnio.time_of_service }
          woundTreatmentsAmnios.time_of_service = `${time_of_service.hour}:${time_of_service.minute}${time_of_service.period}`
        }
        if (treatmentObj.amnio.photos) {
          const photos = { ...treatmentObj.amnio.photos }

          // Encounter level attachments
          // attachment_filepath = photos.graft_image ? photos.graft_image : null
          // attachment_filepath = photos.packaging_image ? photos.packaging_image : null
        }
        if (treatmentObj.amnio.diabetes) {
          const diabetes = { ...treatmentObj.amnio.diabetes }
          woundTreatmentsAmnios.diabetes = diabetes.patient_status ? diabetes.patient_status : null
          woundTreatmentsAmnios.diabetes_management = diabetes.management ? diabetes.management : null
        }
        if (treatmentObj.amnio.wound_dimensions) {
          const wound_dimensions = { ...treatmentObj.amnio.wound_dimensions }
          woundTreatmentsAmnios.dim_length = wound_dimensions.length_cm ? wound_dimensions.length_cm : null
          woundTreatmentsAmnios.dim_width = wound_dimensions.width_cm ? wound_dimensions.width_cm : null
          woundTreatmentsAmnios.dim_depth = wound_dimensions.depth_cm ? wound_dimensions.depth_cm : null
        }
        if (treatmentObj.amnio.product) {
          const amnioGraftsJson = treatmentObj.amnio.product
          const normalize = Array.isArray(amnioGraftsJson) ? amnioGraftsJson : [amnioGraftsJson]

          normalize.forEach(graft => {
            amnio_grafts.push({
              id: uuidv4(),
              treatment_id: treatmentId,
              graft_length: graft.length_cm ? graft.length_cm : null,
              graft_width: graft.width_cm ? graft.width_cm : null,
              graft_qty: graft.quantity ? graft.quantity : null,
              can_be_deleted: true,
            })
          })
        }

        treatment.wound_treatment = {
          ...treatment.wound_treatment,
          wound_treatment_amnio_fixations: woundTreatmentAmnioFixations,
          wound_treatments_amnios: woundTreatmentsAmnios,
          wound_treatments_amnios_research: woundTreatmentsAmniosResearch,
          wound_treatments_amnios_research_symptoms: woundTreatmentsAmniosResearchSymptoms,
          amnio_grafts,
        }
      }

      return treatment
    },
    formatToDermTreatment(treatmentObj) {
      const treatment = {
        ...this.$lodash.cloneDeep(this.dermTreatmentStructure),
        procedure_id: treatmentObj.procedure_performed ? treatmentObj.procedure_performed : null,
      }
      const lesionConditionsList = []
      if (treatmentObj.lesion_condition) {
        const normalize = Array.isArray(treatmentObj.lesion_condition) ? treatmentObj.lesion_condition : [treatmentObj.lesion_condition]
        normalize.forEach(item => {
          const found = this.lesionConditions.find(i => i.title.toLowerCase().includes(item.toLowerCase()))
          if (found) lesionConditionsList.push(found)
        })
      }
      treatment.derm_treatment = {
        lesion_conditions: treatmentObj.lesion_condition ? lesionConditionsList : null,
        lesion_conditions_other: treatmentObj.lesion_condition_other ? treatmentObj.lesion_condition_other : null,
        lesion_description: treatmentObj.lesion_description ? treatmentObj.lesion_description : null,
        topical_agent: treatmentObj.topical_agent ? treatmentObj.topical_agent : null,
        topical_agent_strength: treatmentObj.topical_agent_strength ? treatmentObj.topical_agent_strength : null,
        secondary_topical_agent: treatmentObj.secondary_topical_agent ? treatmentObj.secondary_topical_agent : null,
        secondary_topical_agent_strength: treatmentObj.secondary_topical_agent_strength ? treatmentObj.secondary_topical_agent_strength : null,
        duration: treatmentObj.duration ? treatmentObj.duration : null,
      }
      treatment.operative_note = treatmentObj.procedural_note ? treatmentObj.procedural_note : null

      return treatment
    },
    formatToGtubeTreatment(treatmentObj) {
      const treatment = {
        ...this.$lodash.cloneDeep(this.gtubeTreatmentStructure),
        procedure_id: treatmentObj.procedure_performed ? treatmentObj.procedure_performed : null,
      }
      treatment.g_tube_treatment = {
        abdominal_exam: treatmentObj.abdominal_exam ? treatmentObj.abdominal_exam : null,
        abdominal_exam_other: treatmentObj.abdominal_exam_other ? treatmentObj.abdominal_exam_other : null,
        g_tube_type: treatmentObj.gtube_type ? this.gTubeTypes.find(i => i.title.toLowerCase().includes(treatmentObj.gtube_type.toLowerCase())) : null,
        g_tube_type_other: treatmentObj.gtube_type_other ? treatmentObj.gtube_type_other : null,
        peri_tube_finding: treatmentObj.peri_tube_finding ? treatmentObj.peri_tube_finding : null,
        duration: treatmentObj.duration ? treatmentObj.duration : null,
        peri_tube_tract: treatmentObj.peri_tube_tract ? treatmentObj.peri_tube_tract : null,
        replacement_tube_type: treatmentObj.replacement?.tube_type ? treatmentObj.replacement?.tube_type : null,
        replacement_tube_type_other: treatmentObj.replacement?.tube_type_other ? treatmentObj.replacement?.tube_type_other : null,
        balloon_cc: treatmentObj.replacement?.balloon_cc ? treatmentObj.replacement?.balloon_cc : null,
        replacement_size: treatmentObj.replacement?.replacement_size_french ? treatmentObj.replacement?.replacement_size_french : null,
        replacement_reason: treatmentObj.replacement?.reason ? treatmentObj.replacement?.reason : null,
        replacement_reason_other: treatmentObj.replacement?.reason_other ? treatmentObj.replacement?.reason_other : null,
        verification_of_placement_1: treatmentObj.verification_of_placement?.primary ? treatmentObj.verification_of_placement?.primary : null,
        verification_of_placement_2: treatmentObj.verification_of_placement?.secondary ? treatmentObj.verification_of_placement?.secondary : null,
        verification_of_placement_3: treatmentObj.verification_of_placement?.tertiary ? treatmentObj.verification_of_placement?.tertiary : null,
        verification_of_placement_other: treatmentObj.verification_of_placement?.other ? treatmentObj.verification_of_placement?.other : null,
      }

      treatment.operative_note = treatmentObj.procedural_note ? treatmentObj.procedural_note : null

      return treatment
    },
    formatEncounter(result) {
      this.initialEncounter = { ...this.$lodash.cloneDeep(this.encounter) }
      this.generatedEncounter = {
        advance_care_plan: result.advance_care_plan ? result.advance_care_plan : null,
        is_hospice: result.is_hospice ? result.is_hospice : null,
        visit_reason: result.reason_for_visit ? result.reason_for_visit : null,
        chief_complaint: result.chief_complaint ? result.chief_complaint : null,
        present_illness_history: result.history_of_present_illness ? result.history_of_present_illness : null,
        systems_review: result.review_of_systems ? result.review_of_systems : null,
        body_areas: result.body_areas_examined ? result.body_areas_examined : [],
        mental_status: result.mental_status ? result.mental_status : null,
      }

      if (result.bmi) {
        const bmi = { ...result.bmi }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          height_inches: bmi.height_inches ? bmi.height_inches : null,
          weight_pounds: bmi.weight_pounds ? bmi.weight_pounds : null,
          bmi: bmi.bmi ? bmi.bmi : null,
        }
      }
      if (result.covid_assessment) {
        const covid = { ...result.covid_assessment }
        this.generatedEncounter.encounter_covid_assessment = {
          have_cough: covid.has_cough ? covid.has_cough : null,
          have_fever: covid.has_fever ? covid.has_fever : null,
          have_weakness: covid.has_weakness_lethargy ? covid.has_weakness_lethargy : null,
          test_date: covid.test_date ? covid.test_date : null,
          test_result: covid.test_result ? covid.test_result : null,
          test_type: covid.test_type ? covid.test_type : null,
          other_type: covid.test_type_other ? covid.test_type_other : null,

        }
      }
      if (result.medical_histories) {
        const medical_histories = { ...result.medical_histories }
        const medicalHistoryList = []
        let medicalHistoryOther = ''
        if (medical_histories.selections) {
          const normalize = Array.isArray(medical_histories.selections) ? medical_histories.selections : [medical_histories.selections]
          normalize.forEach(item => {
            const found = this.medicalHistories.find(i => i.title.toLowerCase() === item.toLowerCase())
            if (found) {
              medicalHistoryList.push(found)
            } else medicalHistoryOther += `${item} `
          })
        }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          medical_histories: medicalHistoryList,
          medical_histories_other: medical_histories.other ? `${medical_histories.other}, ${medicalHistoryOther}` : medicalHistoryOther,
          neuropathy: medical_histories.neuropathy ? medical_histories.neuropathy : null,
          neuropathy_type: medical_histories.neuropathy_type ? medical_histories.neuropathy_type : null,
          neuropathy_severity: medical_histories.neuropathy_severity ? medical_histories.neuropathy_severity : null,
        }
      }
      if (result.risk_factors) {
        const risk_factors = { ...result.risk_factors }
        const riskFactorSelections = []
        if (risk_factors.selections) {
          const normalize = Array.isArray(risk_factors.selections) ? risk_factors.selections : [risk_factors.selections]
          normalize.forEach(item => {
            const found = this.riskFactors.find(i => i.title.toLowerCase().includes(item.toLowerCase()))
            if (found) riskFactorSelections.push(found)
          })
        }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          risk_factors: riskFactorSelections,
          risk_factors_other: risk_factors.other ? risk_factors.other : null,
        }
      }
      if (result.family_history) {
        const family_history = { ...result.family_history }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          family_history_contributory: family_history.contributory ? family_history.contributory : [],
          family_history: family_history.other ? family_history.other : null,
        }
      }
      if (result.social_history) {
        const social_history = { ...result.social_history }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          social_history_contributory: social_history.contributory ? social_history.contributory : [],
          social_histories: social_history.drug_use ? social_history.drug_use : null,
          social_history_other: social_history.other ? social_history.other : null,
        }
      }
      if (result.medications) {
        const med = { ...result.medications }
        const medicationsList = []
        if (med.list) {
          const normalize = Array.isArray(med.list) ? med.list : [med.list]
          normalize.forEach(item => {
            const found = this.medications.find(i => i.title.toLowerCase().includes(item.toLowerCase()))
            if (found) medicationsList.push(found)
          })
        }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          medications: med.list ? medicationsList : [],
          medications_other: med.other ? med.other : null,
          therapeutic_anticoagulant: med.therapeutic_anticoagulant ? med.therapeutic_anticoagulant : null,
        }
      }
      if (result.recent_hospitalization) {
        const recent_hospitalization = { ...result.recent_hospitalization }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          recently_hospitalized: recent_hospitalization.has_recently_hospitalized ? recent_hospitalization.has_recently_hospitalized : false,
          recently_hospitalized_related: recent_hospitalization.related_to_wound ? recent_hospitalization.related_to_wound : false,
          recently_hospitalized_date: recent_hospitalization.date_hospitalized ? recent_hospitalization.date_hospitalized : null,
        }
      }
      if (result.mobility_continence) {
        const mobility_continence = { ...result.mobility_continence }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          mobility: mobility_continence.mobility ? mobility_continence.mobility : null,
          mobility_other: mobility_continence.mobility_other ? mobility_continence.mobility_other : null,
          continence: mobility_continence.continence ? mobility_continence.continence : null,
          continence_other: mobility_continence.continence_other ? mobility_continence.continence_other : null,
        }
      }
      if (result.general_appearance) {
        const general_appearance = { ...result.general_appearance }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          general_appearance: general_appearance.mobility ? general_appearance.mobility : [],
          general_appearances_other: general_appearance.mobility_other ? general_appearance.mobility_other : null,
        }
      }
      if (result.vascular_exam) {
        const vascular_exam = { ...result.vascular_exam }
        this.generatedEncounter.encounter_pulse_assessment = {
          femoral_left: vascular_exam.femoral_pulse_left ? vascular_exam.femoral_pulse_left : null,
          femoral_right: vascular_exam.femoral_pulse_right ? vascular_exam.femoral_pulse_right : null,
          popliteal_left: vascular_exam.popliteal_pulse_left ? vascular_exam.popliteal_pulse_left : null,
          popliteal_right: vascular_exam.popliteal_pulse_right ? vascular_exam.popliteal_pulse_right : null,
          posterior_tibial_left: vascular_exam.posterior_tibial_pulse_left ? vascular_exam.posterior_tibial_pulse_left : null,
          posterior_tibial_right: vascular_exam.posterior_tibial_pulse_right ? vascular_exam.posterior_tibial_pulse_right : null,
          dorsalis_pedis_left: vascular_exam.dorsalis_pedis_pulse_left ? vascular_exam.dorsalis_pedis_pulse_left : null,
          dorsalis_pedis_right: vascular_exam.dorsalis_pedis_pulse_right ? vascular_exam.dorsalis_pedis_pulse_right : null,

        }
      }
      if (result.labs) {
        const labs = { ...result.labs }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          labs: labs.selections ? labs.selections : [],
          labs_order_date: labs.date_ordered ? labs.date_ordered : null,
          labs_other: labs.other ? labs.other : null,
        }
      }
      if (result.active_wound_infection) {
        const active_wound_infection = { ...result.active_wound_infection }
        this.generatedEncounter.encounter_active_wound_infection = {
          elevated_temperature: active_wound_infection.elevated_temperature_ruled_other_causes ? active_wound_infection.elevated_temperature_ruled_other_causes : null,
          positive_infection: active_wound_infection.severe_inflammation_or_drainage ? active_wound_infection.severe_inflammation_or_drainage : null,
          inflammation_drainage: active_wound_infection.positive_biopsy ? active_wound_infection.positive_biopsy : null,
          note: active_wound_infection.notes ? active_wound_infection.notes : null,
          cbc: active_wound_infection.cbc ? active_wound_infection.cbc : null,

        }
      }
      if (result.assessment_plan) {
        const assessment_plan = { ...result.assessment_plan }
        this.generatedEncounter.assessment_plan = {
          prognosis: assessment_plan.overall_prognosis ? assessment_plan.overall_prognosis : null,
          prognosis_other: assessment_plan.other_prognosis ? assessment_plan.other_prognosis : null,
          excellent_care: assessment_plan.facility_staff_excellent_care ? assessment_plan.facility_staff_excellent_care : null,
          follow_up_interval: assessment_plan.follow_up.interval ? assessment_plan.follow_up.interval : null,
          follow_up_interval_other: assessment_plan.follow_up.other_interval ? assessment_plan.follow_up.other_interval : null,
          follow_up_interval_reason: assessment_plan.follow_up.frequent_followups_reason ? assessment_plan.follow_up.frequent_followups_reason : null,
          follow_up_activities: assessment_plan.follow_up.actions ? assessment_plan.follow_up.actions : [],
          follow_up_activity_other: assessment_plan.follow_up.other_actions ? assessment_plan.follow_up.other_actions : null,
          goals: assessment_plan.goals.selections ? assessment_plan.goals.selections : null,
          goals_other: assessment_plan.goals.other ? assessment_plan.goals.other : null,
          chronic_wounds: assessment_plan.chronic_wound_indications ? assessment_plan.chronic_wound_indications : null,
          offloading: assessment_plan.offloading.selections ? assessment_plan.offloading.selections : null,
          offloading_other: assessment_plan.offloading.other ? assessment_plan.offloading.other : null,
          mattress: assessment_plan.mattress.suggestion ? assessment_plan.mattress.suggestion : null,
          mattress_types: assessment_plan.mattress.type ? assessment_plan.mattress.type : null,
          mattress_types_other: assessment_plan.mattress.other_type ? assessment_plan.mattress.other_type : null,
          is_on_antibiotics: assessment_plan.antibiotics.on_antibiotics ? assessment_plan.antibiotics.on_antibiotics : null,
          antibiotic_name: assessment_plan.antibiotics.name ? assessment_plan.antibiotics.name : null,
          antibiotic_dose: assessment_plan.antibiotics.dose ? assessment_plan.antibiotics.dose : null,
          antibiotic_route: assessment_plan.antibiotics.route ? assessment_plan.antibiotics.route : null,
          antibiotic_date_started: assessment_plan.antibiotics.date_started ? assessment_plan.antibiotics.date_started : null,
          antibiotic_duration: assessment_plan.antibiotics.duration ? assessment_plan.antibiotics.duration : null,
          assessment_plan: assessment_plan.text ? assessment_plan.text : null,

        }
      }
      if (result.vitals) {
        const vitals = { ...result.vitals }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          is_patient_refused_vitals: vitals.patient_refused_vitals ? vitals.patient_refused_vitals : null,
          temperature: vitals.temperature ? vitals.temperature : null,
          temperature_scale: vitals.temperature_unit ? vitals.temperature_unit : null,
          heart_rate: vitals.heart_rate ? vitals.heart_rate : [],
          respiratory_rate: vitals.respiratory_rate ? vitals.respiratory_rate : null,
          bp_systolic: vitals.bp_systolic ? vitals.bp_systolic : null,
          bp_diastolic: vitals.bp_diastolic ? vitals.bp_diastolic : null,
        }
      }
      if (result.dfu_risk_review) {
        const dfu_risk_review = { ...result.dfu_risk_review }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          dfu_risk: dfu_risk_review.dfu_indicators ? dfu_risk_review.dfu_indicators : null,
          dfu_risk_reason: dfu_risk_review.unable_to_accept_reason ? dfu_risk_review.unable_to_accept_reason : null,
          a1c_level: dfu_risk_review.a1c_level ? dfu_risk_review.a1c_level : null,
        }
      }
      if (result.footwear_eval) {
        const footwear_eval = { ...result.footwear_eval }
        this.generatedEncounter = {
          ...this.generatedEncounter,
          footwear_evaluation: footwear_eval.evaluation ? footwear_eval.evaluation : null,
          footwear_evaluation_reason: footwear_eval.unable_to_accept_reason ? footwear_eval.unable_to_accept_reason : null,
          footwear_notable_issues: footwear_eval.notable_issues ? footwear_eval.notable_issues : null,
          footwear_exam_reason: footwear_eval.footwear_change_recommended ? footwear_eval.footwear_change_recommended : [],
        }
      }
    },
    calcWoundSize(primaryLength, primaryWidth, primaryDepth) {
      let size = null

      if (this.is_healed) {
        size = 'Wound resolved'
      } else if (this.$custom.notEmpty(primaryLength) && this.$custom.notEmpty(primaryWidth) && this.$custom.notEmpty(primaryDepth) && primaryDepth === 'UTD') {
        size = `${primaryLength} cm x ${primaryWidth} cm x UTD`
      } else if (this.$custom.notEmpty(primaryLength) && this.$custom.notEmpty(primaryWidth) && this.$custom.notEmpty(primaryDepth)) {
        size = `${primaryLength} cm x ${primaryWidth} cm x ${primaryDepth} cm`
      } else if (this.$custom.notEmpty(primaryLength) && this.$custom.notEmpty(primaryWidth)) {
        size = `${primaryLength} cm x ${primaryWidth} cm`
      }

      return size
    },

    resetWounds() {
      this.$root.confirm({
        title: 'Reset Existing and Generated Wounds?',
        body: 'Do you wish to undo any changes to existing and generated wounds?',
        cancel: 'Cancel',
        cancelColor: 'secondary',
        cancelIcon: this.icons.mdiLogoutVariant,
        confirm: 'Reset',
      }).then(result => {
        if (result === true) {
          this.wounds = [...this.$lodash.cloneDeep(this.initialExistingWounds)]
          this.$emit('input', [...this.wounds])
          this.mergedWounds = [...this.initialMergedWounds]
          this.generatedWounds = [...this.initialGeneratedWounds]
        }
      })
    },
    saveWounds() {
      this.$root.confirm({
        title: 'Save Changes to Existing and Generated Wounds?',
        body: 'Do you wish to save any changes to existing and generated wounds?',
        cancel: 'Cancel',
        cancelColor: 'secondary',
        cancelIcon: this.icons.mdiLogoutVariant,
        confirm: 'Save',
        confirmColor: 'success',
      }).then(result => {
        if (result === true) {
          const filteredGeneratedWounds = this.generatedWounds.filter(x => !this.mergedWounds.some(i => i.generatedId === x.id))
          this.wounds = this.wounds.concat(filteredGeneratedWounds)
          this.$emit('input', [...this.wounds])
          this.$emit('update:wound-treatment-updated', true)
          this.mergedWounds = []

          const mergedEncounter = mergeIfEmpty(this.initialEncounter, this.generatedEncounter)

          this.$emit('update:encounter', mergedEncounter)
          this.$store.commit('encounters/updateEncounter', { encounter: mergedEncounter })

          this.$emit('update:wound-mapping-modal', false)

          window.location.reload()
        }
      })
    },
    closeModal() {
      this.$root.confirm({
        title: 'Discard generated wounds?',
        body: 'Do you wish to discard these wounds?',
        cancel: 'Cancel',
        cancelColor: 'secondary',
        cancelIcon: this.icons.mdiLogoutVariant,
        confirm: 'Confirm',
      }).then(result => {
        if (result === true) {
          this.wounds = [...this.$lodash.cloneDeep(this.initialExistingWounds)]
          this.$emit('input', [...this.wounds])
          this.generatedWounds = [...this.$lodash.cloneDeep(this.initialGeneratedWounds)]
          this.mergedWounds = []

          this.$emit('update:wound-mapping-modal', false)
        }
      })
    },
    viewJson() {
      this.viewJsonModalState = true
    },
    copyJsonToClipboard() {
      this.$refs.json.select()
      document.execCommand('copy')
    },
  },
}
</script>

<style scoped>
.full-width-chip {
  width: 100%;
  display: block;
  margin: 0 auto;
  font-size: 16px;
  min-height: auto !important;
  height: auto !important;
}
::v-deep .full-width-chip .v-chip__content {
  color: black;
}
</style>
